import React from "react";

interface Tip {
  number: number;
  title: string;
  mainPoint?: string;
  lookFor?: string[];
  redFlags?: string[];
  greenFlags?: string[];
  additionalPoints?: string[];
}

const TipBlock: React.FC<{ tip: Tip }> = ({ tip }) => {
  return (
    <article className="w-full">
      {/* Header */}
      <div className="relative">
        {/* Rounded card header (NOT full pill) */}
        <div className="w-full rounded-[18px] bg-[#192842] px-6 py-5 pl-[92px]">
          <h2 className="text-white font-extrabold tracking-[0.2px] lg:text-[25px] text-[18px] leading-[32px]">
            {tip.title}
          </h2>
        </div>

        {/* Circle overlapping */}
        <div className="absolute -left-[10px] -top-[5px]">
          <div className="w-[83px] h-[83px] rounded-full bg-[#0B6054] border-[5px] border-white flex items-center justify-center ">
            <span className="text-white font-extrabold text-[56px] leading-none">
              {tip.number}
            </span>
          </div>
        </div>
      </div>

      {/* Body (no indent on mobile; indent only on md+) */}
      <div className="mt-6 text-[#1F2A44] max-w-[760px] md:ml-[86px]">
        {tip.mainPoint?.trim() ? (
          <p
            className="text-[18px] font-[500] leading-[32px] mb-5"
            dangerouslySetInnerHTML={{
              __html:
                tip.mainPoint +
                (tip.mainPoint.trim().endsWith(".") ||
                tip.mainPoint.trim().endsWith(":") ||
                tip.mainPoint.trim().endsWith(":</strong>") ||
                tip.mainPoint.trim().endsWith(":</strong>.")
                  ? ""
                  : "."),
            }}
          />
        ) : null}

        {tip.lookFor?.length ? (
          <div className="mt-2">
            {tip.number !== 6 && (
              <p className="text-[18px] font-[700] mb-2">Look for:</p>
            )}
            <ul className="list-disc pl-7 text-[18px] font-[500] leading-[32px]">
              {tip.lookFor.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {tip.additionalPoints?.length ? (
          <div className={`${tip.lookFor?.length ? "mt-5" : ""} space-y-4`}>
            {tip.additionalPoints.map((p, i) => (
              <p
                key={i}
                className="text-[18px] font-[500] leading-[32px]"
                dangerouslySetInnerHTML={{ __html: p }}
              />
            ))}
          </div>
        ) : null}

        {tip.redFlags?.length ? (
          <div className="mt-6">
            <p className="text-[18px] font-[500] leading-[32px]">
              <span className="font-[800]">Red flags:</span>{" "}
              {tip.redFlags.join(" ")}
            </p>
          </div>
        ) : null}

        {tip.greenFlags?.length ? (
          <div className="mt-6 space-y-3">
            {tip.greenFlags.map((g, i) => (
              <p key={i} className="text-[18px] font-[500] leading-[32px]">
                {g}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
};


const TipsSection: React.FC = () => {
  const tips: Tip[] = [
    {
      number: 1,
      title: "Prioritize Winning Management Teams",
      mainPoint: "People drive outcomes in junior mining more than any other factor.",
      lookFor: [
        "Capital markets experience — teams that raise efficiently with minimal dilution",
        "CEOs who excel at storytelling and financing (technical roles better filled by specialist geologists)",
        "Proven success — prior exits or mines built/sold at premiums",
      ],
      redFlags: [
        `Generous "sweeteners" like full warrants at low strikes valid for 3+ years (signals desperation and future overhang). Avoid teams with a history of heavy dilution or no meaningful wins.`,
      ],
    },
    {
        number: 2,
        title: "Focus on Cohesive Project Portfolios",
        additionalPoints: [
            "Prefer companies with 1–5 core projects where management shows clear conviction.", 
            "Too many assets (>10) often means lack of focus and spreading capital too thin.", 
            "Early-stage land packages without systematic exploration rarely deliver quickly.",
            
        ],
        greenFlags: ["Green flag:  Consistent news flow from 1–2 flagship projects."],
      },
    {
      number: 3,
      title: "Demand Strong Stakeholder Relationships",
      additionalPoints: [
        "Jurisdiction matters. Favor stable, pro-mining regions with clear permitting paths.",
        "<strong>Beyond government:</strong> Local community and First Nations support is essential.", 
        "Companies that invest in relationships early de-risk significantly.",
        "Avoid projects with evident opposition or opaque local dynamics.",
      ],
    },
    {
      number: 4,
      title: 'Beware of "Near-ology" Hype',
      additionalPoints: [
        'Slides heavy on neighboring mines ("we\'re next to XYZ\'s billion-ounce deposit") without substantive drilling are <strong>common traps</strong>.',
        'Location helps, but only drilling proves what\'s underground. Excessive <strong>"near-ology"</strong> without results usually signals weak fundamentals.',
      ],
    },
    {
      number: 5,
      title: "Understand Drill Results Properly",
      additionalPoints: [
        'Use the <strong>"200 gram-meter rule"</strong> for gold: Interval width x grade should approach or exceed 200 for standout hits.',
        "Prefer wider intervals over narrow ultra-high-grade (easier to mine). Always check surrounding assays for continuity — isolated bonanza hits rarely build mines.",
      ],
    },
    {
      number: 6,
      title: "Scrutinize Economic Studies Carefully",
      mainPoint: "<strong>Stress-test assumptions:</strong>",
      lookFor: [
        "Use conservative metal prices ($2,000-$2,500 gold equivalent)",
        "Post-tax NPV and IRR",
        "Capex <150% of NPV",
        "Reasonable contingencies",
      ],
      additionalPoints: [
        "Projects only economic at spot or higher carry high execution risk.",
      ],
    },
    {
      number: 7,
      title: "Stay Focused on Core Thesis",
      additionalPoints: [
        "Companies pivoting from gold to uranium to lithium to crypto often lack genuine focus.",
        "Stick with teams committed to their core thesis.",
      ],
    },
    {
      number: 8,
      title: "Steer Clear of True Penny Stocks",
      additionalPoints: [
        'Stocks consistently below $0.20 or market caps <$10M are often <strong>"fallen angels"</strong> — failed explorers unable to fund properly.',
        "Higher probability of consolidation or cessation.",
      ],
    },
    {
        number: 9,
        title: "Watch Share Count and Structure",
        additionalPoints: [
            "<strong>Explorers:</strong> Ideally <50–70M shares outstanding",
            "<strong>Developers:</strong> <100–150M reasonable",
            "Heavy warrant/option overhang or rapid share growth signals past poor financing.",
        ],
      },
    {
      number: 10,
      title: "Spread Your Bets Wisely",
      additionalPoints: [
        "Never fewer than 5-6 juniors for <strong>diversification</strong>.",
        "10-15 total positions allows meaningful sizing in convictions while managing risk.",
      ],
    },
  ];

  return (
    <section className="bg-white py-14 md:py-20">
      {/* matches the centered “page” look */}
      <div className="mx-auto w-full max-w-[980px] px-6 md:px-10">
        <div className="space-y-10 md:space-y-14">
          {tips.map((tip) => (
            <TipBlock key={tip.number} tip={tip} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TipsSection;
